// アプリタイプの定義
export const APP_TYPES = {
  MEDILAB: 'Medilab',
  TMN: 'TMN',
  MELPHIN: 'Melphin',
} as const

export type AppType = (typeof APP_TYPES)[keyof typeof APP_TYPES]

// annotator判定用のヘルパー（アプリタイプとは独立）
export const isAnnotatorUser = (): boolean => {
  if (typeof window === 'undefined') return false
  const isAnnotator = localStorage.getItem('is_annotator')
  return isAnnotator === 'true'
}

// アプリタイプのグループ判定用ヘルパー
export const isMediLabApp = (appType: AppType): boolean => {
  return appType === APP_TYPES.MEDILAB
}

export const isTMNApp = (appType: AppType): boolean => {
  return appType === APP_TYPES.TMN
}

export const isMelphinApp = (appType: AppType): boolean => {
  return appType === APP_TYPES.MELPHIN
}

// ============================================================
// コンポーネントマッピング用の型定義
// ============================================================

/**
 * 患者情報UIのタイプ
 * - PatientMatching: PatientMatchingBasicResultWindow + NonMatchingWithOtherTab
 * - Merged: MergedBasicResultWindow
 * - Melphin: MelphinPatientMatching + MelphinNonMatching
 *
 * 注: Annotatorは NonMatchingBasicResultWindowWithOtherTab のみを使用（AppTypeとは独立）
 */
export type BasicResultWindowType = 'PatientMatching' | 'Merged' | 'Melphin'

/**
 * 薬品結果UIのタイプ
 * - ColumnBased: ColumnBasedMedicineResult（カラム形式）
 * - RPBased: RPBasedMedicineResult（RP単位）
 */
export type MedicineResultType = 'ColumnBased' | 'RPBased'

/**
 * ドロワーのタイプ
 * - MediLab: MediLabDrawerToggleButton
 * - TMN: DrawerToggleButton（共通）
 * - Melphin: MelphinDrawerToggleButton
 *
 * 注: Annotatorは AnnotateDrawerButtons を使用（AppTypeとは独立）
 */
export type DrawerType = 'MediLab' | 'TMN' | 'Melphin'

/**
 * ヘルプアイコンのタイプ
 * - HelpOutline: HelpOutlineIcon（?マーク）
 * - Campaign: CampaignRoundedIcon（旗）
 */
export type HelpIconType = 'HelpOutline' | 'Campaign'

/**
 * useReportPrescフックのタイプ
 * - MediLab: apps/medilab/hooks/useReport
 * - TMN: apps/tmn/hooks/useReport
 * - Melphin: apps/melphin/hooks/useReport
 *
 * 注: Annotatorは apps/annotator/hooks/useReport を使用（AppTypeとは独立）
 */
export type ReportHookType = 'MediLab' | 'TMN' | 'Melphin'

// ============================================================
// AppConfig インターフェース
// ============================================================

export interface AppConfig {
  // サービス情報
  serviceName: string
  shortServiceName: string
  copyrightText: (year: number) => string
  theme: 'medilab' | 'tmn'

  // アセット
  assets: {
    logo: 'medilab' | 'tmn'
    maintenanceImage?: 'cat' | 'tmn'
  }

  // コンポーネントマッピング
  components: {
    basicResultWindow: BasicResultWindowType
    medicineResult: MedicineResultType
    drawer: DrawerType
    helpIcon: HelpIconType
    reportHook: ReportHookType
  }

  // 機能フラグ
  features: {
    portalButton: boolean // ポータルボタン表示
    shortConfirmationModal: boolean // 短確認モーダル（ホバー時）
    helpModal: boolean // ヘルプモーダル
  }

  // ルート設定
  routes: {
    login: boolean
    portal: boolean
    devReport: boolean
    support: boolean
  }
}

// ============================================================
// APP_CONFIGS
// ============================================================

export const APP_CONFIGS: Record<AppType, AppConfig> = {
  [APP_TYPES.MEDILAB]: {
    serviceName: '調剤入力支援AI「まもる君」',
    shortServiceName: 'まもる君',
    copyrightText: (year) => `Copyright © Medilab. ${year}. ver.3.1.2`,
    theme: 'medilab',
    assets: {
      logo: 'medilab',
      maintenanceImage: 'cat',
    },
    components: {
      basicResultWindow: 'PatientMatching',
      medicineResult: 'ColumnBased',
      drawer: 'MediLab',
      helpIcon: 'HelpOutline',
      reportHook: 'MediLab',
    },
    features: {
      portalButton: true,
      shortConfirmationModal: false,
      helpModal: true,
    },
    routes: {
      login: true,
      portal: true,
      devReport: true,
      support: true,
    },
  },
  [APP_TYPES.TMN]: {
    serviceName: 'RXクラウド',
    shortServiceName: 'RXクラウド',
    copyrightText: (year) =>
      `© ${year} Transaction Media Networks Inc. All Rights Reserved`,
    theme: 'tmn',
    assets: {
      logo: 'tmn',
      maintenanceImage: 'tmn',
    },
    components: {
      basicResultWindow: 'Merged',
      medicineResult: 'RPBased',
      drawer: 'TMN',
      helpIcon: 'Campaign',
      reportHook: 'TMN',
    },
    features: {
      portalButton: false,
      shortConfirmationModal: true,
      helpModal: false,
    },
    routes: {
      login: true,
      portal: false,
      devReport: false,
      support: false,
    },
  },
  [APP_TYPES.MELPHIN]: {
    serviceName: '調剤入力支援AI「まもる君」',
    shortServiceName: 'まもる君',
    copyrightText: (year) => `Copyright © Medilab. ${year}. ver.3.1.2`,
    theme: 'medilab',
    assets: {
      logo: 'medilab',
      maintenanceImage: 'cat',
    },
    components: {
      basicResultWindow: 'Melphin',
      medicineResult: 'ColumnBased',
      drawer: 'Melphin',
      helpIcon: 'HelpOutline',
      reportHook: 'Melphin',
    },
    features: {
      portalButton: true,
      shortConfirmationModal: false,
      helpModal: true,
    },
    routes: {
      login: true,
      portal: true,
      devReport: true,
      support: true,
    },
  },
}
